/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { useContext, useEffect, useRef, useState, cloneElement } from 'react';
import { useSelector } from 'react-redux';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import { getButtonFocus } from 'styles/variables';
import Face from './Face';
import { interactionContext } from './interactionContext';
import { State } from 'store/store';

import { HostingContext } from 'hosting/context';
import { flipcardEvent } from 'hosting/events';

const Flipcard = props => {
  const { id, updateFlipCardSeen, numFlipCards, index, children } = props;
  const [cardHasFocus, setCardHasFocus] = useState(false);

  const [{ rotation, visibleFace }, updateCardDisplay] = useState({
    rotation: 0,
    visibleFace: 'front',
  });

  const [cardContent, setCardContent] = useState<string | undefined>();

  let [frontFaceMedia, backFaceMedia] = children;

  const [edgeOpacityHack, setEdgeOpacityHack] = useState(0);

  const context = useContext(interactionContext);
  const hostingCtx = useContext(HostingContext);

  const {
    includeFlipcardBackground = true,
    flipcardFrontColor,
    flipcardBackColor,
    flipcardFrontTextColor,
    flipcardBackTextColor,
    flipcardBackgroundOpacity = 100,
    focusColor,
    flipcardWidth,
    flipcardHeight,
    flipcardOutline = false,
    flipcardOutlineColor = '#282A2B',
    flipcardBorderRadius = 10,
    alignTextTop,
    includeShadow = true,
  } = context.props;

  const flipCard = useRef<HTMLDivElement>(null);
  const flipCardFaces = useRef<any>(undefined);
  const flipCardInit = useRef(false);
  const hiddenButton = useRef<HTMLButtonElement>(null);
  const delayReadingCardContent = useRef<number>(0);

  const frontFaceMediaIsSVG = frontFaceMedia?.props?.child === 3;
  const backFaceMediaIsSVG = backFaceMedia?.props?.child === 3;

  const frontFaceMediaIsSVGKey = useRef('front');
  const backFaceMediaIsSVGKey = useRef('back');

  const isOpen = useSelector((state: State) => {
    return state.interactionsOpen.includes(context.id);
  });

  const updateRotation = e => {
    window.dev() && checkReturnToInteractionEditing(true);

    if (visibleFace !== 'all') {
      // we only want these keys to update when the visibleFace is front or back
      if (visibleFace !== frontFaceMediaIsSVGKey.current) {
        frontFaceMediaIsSVGKey.current = visibleFace;
      }

      if (visibleFace !== backFaceMediaIsSVGKey.current) {
        backFaceMediaIsSVGKey.current = visibleFace;
      }
    }

    let bounds;
    let left;
    let width;
    let side = 'left';

    if (e) {
      bounds = e.currentTarget.getBoundingClientRect();
      left = bounds.left;
      width = bounds.width;
      side = e.clientX < left + width / 2 ? 'left' : 'right';
    }

    updateCardDisplay({
      rotation: rotation + (side === 'left' ? -180 : 180),
      visibleFace: 'all',
    });

    setEdgeOpacityHack(edgeOpacityHack => (edgeOpacityHack === 0 ? 1 : 0));

    updateFlipCardSeen(id);

    if (window.dev()) {
      // && hostingCtx?.data
      return;
    }

    const flipcard_flip = {
      flipcardId: id,
    };

    hostingCtx?.data(flipcardEvent(context.id, flipcard_flip));

    // console.log(flipcard_flip);
  };

  const updateCardContent = (
    face: 'front' | 'back' | undefined = undefined
  ) => {
    if (!flipCard.current) {
      return;
    }

    const faceElement = flipCard.current.querySelector(
      `#${visibleFace}-${id}`
    ) as HTMLElement | null;

    if (!faceElement) {
      return;
    }

    let faceContent: string | undefined = faceElement.innerText;

    if (!faceContent) {
      const img = faceElement.querySelector('img');

      if (img) {
        faceContent = `${String(img.getAttribute('alt'))}`;
      }
    }

    if (faceContent && !/\.$/.test(faceContent)) {
      // console.log(
      //   "if faceContent doesn't end with a period. we add it so screenReader pauses appropriately"
      // );

      faceContent = `${faceContent}.`;
    }

    setCardContent(faceContent || '');
  };

  const flipCardBaseShadow = '0 8px 24px 5px rgba(0, 0, 0, 0.2)';

  const getFocusColor = () => {
    if (focusColor) {
      return focusColor;
    }

    return (Math.abs(rotation) / 180) % 2 == 0
      ? ['#fff', '#ffffff', 'rgb(255, 255, 255)'].includes(flipcardFrontColor)
        ? flipcardBackColor
        : flipcardFrontColor
      : flipcardBackColor;
  };

  const getFocusColor_2 = () =>
    getButtonFocus(getFocusColor(), flipCardBaseShadow)
      .replace(/box-shadow:|;|!important/g, '')
      .trim();

  useEffect(() => {
    if (!flipCardFaces.current) {
      flipCardFaces.current = Array.from(flipCard.current!.children);
    }

    const onTransitionEnd = e => {
      const visibleFace =
        (Math.abs(rotation) / 180) % 2 === 1 ? 'back' : 'front';

      // if we want to NOT use state. . .
      const hiddenCardElement: any = flipCardFaces.current.filter(element =>
        element.id.includes(visibleFace)
      )[0];

      if (flipCardInit.current) {
        updateCardDisplay({
          rotation,
          visibleFace,
        });
        flipCard.current!.removeEventListener('transitionend', onTransitionEnd);
      }

      flipCardInit.current = true;
    };

    flipCard.current!.addEventListener('transitionend', onTransitionEnd);

    return () => {
      flipCard.current!.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [rotation]);

  useEffect(() => {
    if (visibleFace === 'all') {
      return;
    }

    clearTimeout(delayReadingCardContent.current);

    delayReadingCardContent.current = window.setTimeout(() => {
      updateCardContent();
    }, 500);

    return () => {
      clearTimeout(delayReadingCardContent.current);
    };
  }, [visibleFace]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const texts = Array.from(
      flipCard.current!.querySelectorAll(`[class*="flipcard-text"]`)
    ) as HTMLElement[];

    const padding = 60;

    const checkFlipCardSize = () => {
      if (window.innerWidth - padding <= flipcardWidth) {
        flipCard.current!.style.width = `calc(100vw - ${padding}px)`;

        if (texts) {
          flipCard.current!.style.minHeight = `${Math.max(
            ...texts.map(t => t.offsetHeight)
          )}px`;
        }

        return;
        //
      }

      if (flipCard.current!.style.width === `${flipcardWidth}px`) {
        return;
      }

      flipCard.current!.style.width = `${flipcardWidth}px`;
      flipCard.current!.style.height = `${flipcardHeight}px`;
    };

    checkFlipCardSize();

    // we're just tapping the checkFlipCardSize a couple times
    // useful if flipCard interaction is the first interaction in topic
    let counter = 0;
    const tick = setInterval(() => {
      counter += 1;

      if (counter >= 3) {
        // console.log('counter has checked to resize flipCard 3x @ 12fps');
        clearInterval(tick);
      }

      checkFlipCardSize();
    }, 1000 / 12);
    //

    window.addEventListener('resize', checkFlipCardSize);

    return () => {
      clearInterval(tick);
      window.removeEventListener('resize', checkFlipCardSize);
    };
  }, [isOpen, visibleFace]);

  return (
    <div
      ref={flipCard}
      id={`i${id}`}
      css={css`
        background: transparent;

        border-radius: ${flipcardBorderRadius}px;
        width: ${flipcardWidth}px;
        height: ${flipcardHeight}px;
        margin: 10px;
        perspective: 1000px;
        cursor: pointer;

        .chameleon-image,
        .chameleon-image > div,
        .chameleon-image img:not(.zoom-image img) {
          position: absolute;
          left: 0px;
          top: 0px;

          width: 100% !important;
          height: 100% !important;

          max-width: ${flipcardWidth}px !important;
          max-height: ${flipcardHeight}px !important;
        }

        .zoom-image {
          bottom: 5px;
          right: ${5 + flipcardBorderRadius / 2}px;
          transform: scale(0.8);
        }

        ${cardHasFocus &&
          `
        #${visibleFace}-${id} {
          ${getButtonFocus(
            getFocusColor(),
            '0 8px 24px 5px rgba(0, 0, 0, 0.2)'
          )}
        }
        `}
      `}
      onClick={(e: any) => {
        e.stopPropagation();

        updateRotation(e);
        // return from the click. do not focus button
        return;

        if (hiddenButton.current) {
          requestAnimationFrame(() => hiddenButton.current!.focus());
        }
      }}
      role="listitem"
    >
      {!window.dev() && (
        <button
          ref={hiddenButton}
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            width: 0;
            height: 0;
            border: 0;
            outline: none;
            overflow: hidden;
          `}
          onFocus={e => {
            updateCardContent();
            //
            setCardHasFocus(true);
          }}
          onBlur={() => {
            setCardHasFocus(false);
          }}
          onClick={e => {
            e?.stopPropagation();
            updateRotation(null);
          }}
          aria-pressed={visibleFace !== 'front'}
          aria-label={`flip card ${index + 1} of ${numFlipCards}.${
            cardContent ? ` ${cardContent}` : ''
          }`}
        />
      )}

      <Face
        id={`front-${id}`}
        face="front"
        color={flipcardFrontColor}
        includeFlipcardBackground={includeFlipcardBackground}
        cardBackgroundOpacity={flipcardBackgroundOpacity}
        includeShadow={includeShadow}
        textColor={flipcardFrontTextColor}
        alignTextTop={alignTextTop}
        flipcardOutline={!!flipcardOutline}
        flipcardOutlineColor={flipcardOutlineColor}
        flipcardBorderRadius={flipcardBorderRadius}
        zIndex={1}
        transform={`rotateY(${rotation}deg)`}
        {...{
          opacity: edgeOpacityHack === 1,
        }}
        visibleFace={visibleFace}
        visibility={['front', 'all'].includes(visibleFace)}
      >
        {frontFaceMediaIsSVG
          ? cloneElement(frontFaceMedia, {
              key: frontFaceMediaIsSVGKey.current,
            })
          : frontFaceMedia}
      </Face>
      <Face
        id={`back-${id}`}
        face="back"
        color={flipcardBackColor}
        includeFlipcardBackground={includeFlipcardBackground}
        cardBackgroundOpacity={flipcardBackgroundOpacity}
        includeShadow={includeShadow}
        textColor={flipcardBackTextColor}
        alignTextTop={alignTextTop}
        flipcardOutline={!!flipcardOutline}
        flipcardOutlineColor={flipcardOutlineColor}
        flipcardBorderRadius={flipcardBorderRadius}
        zIndex={2}
        transform={`rotateY(${rotation + 180}deg)`}
        {...{
          opacity: edgeOpacityHack === 0,
        }}
        visibleFace={visibleFace}
        visibility={['back', 'all'].includes(visibleFace)}
      >
        {backFaceMediaIsSVG
          ? cloneElement(backFaceMedia, {
              key: backFaceMediaIsSVGKey.current,
            })
          : backFaceMedia}
      </Face>
    </div>
  );
};

export default Flipcard;
